import { useEffect, useRef } from "react";

/**
 * Inline Calendly embed.
 * Loads Calendly widget.js once and renders the inline widget.
 */
export const CalendlyEmbed = ({
    url = "https://calendly.com/growthx-info/30min",
    minHeight = 700,
    testId = "calendly-inline-widget",
}) => {
    const containerRef = useRef(null);

    useEffect(() => {
        // Load Calendly CSS once
        const linkId = "calendly-widget-css";
        if (!document.getElementById(linkId)) {
            const link = document.createElement("link");
            link.id = linkId;
            link.rel = "stylesheet";
            link.href =
                "https://assets.calendly.com/assets/external/widget.css";
            document.head.appendChild(link);
        }

        // Initialize this specific widget once Calendly is available.
        // Calendly auto-inits .calendly-inline-widget elements only on first
        // script load — dynamically-mounted widgets need explicit init.
        const initWidget = () => {
            if (
                window.Calendly &&
                containerRef.current &&
                !containerRef.current.querySelector("iframe")
            ) {
                window.Calendly.initInlineWidget({
                    url,
                    parentElement: containerRef.current,
                });
            }
        };

        const scriptId = "calendly-widget-script";
        const existing = document.getElementById(scriptId);
        if (!existing) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://assets.calendly.com/assets/external/widget.js";
            script.async = true;
            script.onload = initWidget;
            document.body.appendChild(script);
        } else if (window.Calendly) {
            initWidget();
        } else {
            existing.addEventListener("load", initWidget);
        }

        // Listen for booking completion event
        const handleMessage = (e) => {
            if (
                e.origin === "https://calendly.com" &&
                e.data?.event === "calendly.event_scheduled"
            ) {
                // Fire custom analytics event
                if (window.plausible) {
                    window.plausible("calendar_booking_complete");
                }
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    return (
        <div
            ref={containerRef}
            className="calendly-inline-widget w-full rounded-xl overflow-hidden border border-border bg-white"
            data-url={url}
            data-testid={testId}
            style={{ minWidth: "320px", height: `${minHeight}px` }}
        />
    );
};

export default CalendlyEmbed;
